// Cache local (localStorage) des données consultables hors ligne. Purgé à la déconnexion.
const PREFIX = 'ap-offline:'
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

export interface OfflineEntry<T> {
  data: T
  savedAt: number
}

export function readOfflineCache<T>(key: string): OfflineEntry<T> | null {
  if (!import.meta.client) {
    return null
  }

  try {
    const raw = localStorage.getItem(PREFIX + key)
    if (!raw) {
      return null
    }

    const entry = JSON.parse(raw) as OfflineEntry<T>
    if (!entry || typeof entry.savedAt !== 'number' || Date.now() - entry.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(PREFIX + key)
      return null
    }

    return entry
  }
  catch {
    return null
  }
}

export function writeOfflineCache<T>(key: string, data: T) {
  if (!import.meta.client) {
    return
  }

  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ data, savedAt: Date.now() }))
  }
  catch {
    // Stockage plein ou indisponible : la page fonctionne sans cache.
  }
}

export function clearOfflineCache() {
  if (!import.meta.client) {
    return
  }

  try {
    Object.keys(localStorage)
      .filter(key => key.startsWith(PREFIX))
      .forEach(key => localStorage.removeItem(key))
  }
  catch {
    // Stockage indisponible : rien à purger.
  }

  // Images mises en cache par le service worker
  globalThis.caches?.delete('ap-images').catch(() => {})
}

/**
 * Exécute `fetcher` et mémorise le résultat ; en cas de panne réseau (ou 5xx), renvoie la dernière copie locale.
 * Les erreurs applicatives (401, 403, 404...) sont relancées telles quelles.
 */
export async function fetchWithOfflineCache<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  try {
    const data = await fetcher()
    writeOfflineCache(key, data)
    return data
  }
  catch (error: any) {
    const status = Number(error?.statusCode ?? error?.status ?? error?.response?.status)
    const cached = !status || status >= 500 ? readOfflineCache<T>(key) : null
    if (cached) {
      return cached.data
    }
    throw error
  }
}
